/* eslint-disable camelcase */

// node modules
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';

// app modules: components
import { ContentOptionsAsIcon } from 'components/ContentOptions';
import AlacarteStatusIndicator from 'components/AlacarteStatusIndicator';
import BookRankOverlay from 'components/BookRankOverlay';
import FavouriteButton from 'components/FavouriteButton';
import Link from 'components/Link';
import Price from 'components/Price';

// app modules: services
import assets from 'services/assets';
import route from 'services/route';
import formatter from 'services/formatter';

// app modules: constants
import { CONTENT_TYPES } from 'constants/index';

// assets
import CssCollectionBookItem from './BookItem.scss';

const COVER_ASPECT_RATIO = 3 / 4;

class CollectionBookItem extends PureComponent {
  static propTypes = {
    backText: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    className: PropTypes.string,
    collectionId: PropTypes.number,
    hideActions: PropTypes.bool,
    item: PropTypes.shape({
      checked: PropTypes.bool,
      image_name: PropTypes.string,
      isbn: PropTypes.string,
      signature_: PropTypes.string,
    }).isRequired,
    onClick: PropTypes.func,
    owned: PropTypes.bool,
    withMenu: PropTypes.bool,
  };

  static defaultProps = {
    backText: null,
    children: () => null,
    className: null,
    collectionId: null,
    hideActions: false,
    onClick: () => {},
    owned: false,
    withMenu: false,
  };

  render() {
    const {
      backText,
      children,
      className,
      collectionId,
      hideActions,
      item,
      onClick,
      owned,
      withMenu,
    } = this.props;
    if (!item) {
      return (
        <div className="collection-book-item">
          <Skeleton height={100} />
        </div>
      );
    }
    const {
      alacarte,
      alacarte_available,
      authors,
      checked,
      isbn,
      image_aspect_ratio,
      image_name,
      price,
      ranking_recommendations,
      signature_,
      title,
    } = item;

    const toRenderAuthors = formatter.authorsList.toString(authors);
    const rootClassName = classNames('collection-book-item', className, {
      'collection-book-item--checked': checked,
      'collection-book-item--with-menu': withMenu,
    });
    const image = (
      <figure className="collection-book-item__figure">
        <img
          src={assets.coverUrl(image_name, {
            resize: 'fit-in',
            height: 100,
            width: 100,
          })}
          srcSet={assets.srcSet(
            image_name,
            [{ w: 100, h: 100, resize: 'fit-in' }, { w: 200, h: 200, resize: 'fit-in' }],
            assets.coverUrl,
          )}
          className="collection-book-item__image"
          width={image_aspect_ratio > COVER_ASPECT_RATIO ? 80 * COVER_ASPECT_RATIO : null}
          height={image_aspect_ratio <= COVER_ASPECT_RATIO ? 80 : null}
          alt={title}
          loading="lazy"
        />
      </figure>
    );

    let priceSection = null;
    if (!owned && price) {
      const priceEl = (
        <Price
          currency={price.currency}
          amount={price.offer_price}
          originalPrice={price.list_price}
        />
      );
      if (price.discount_text) {
        priceSection = (
          <p>
            <span className="collection-book-item__discount-text">
              {priceEl} {price.discount_text}
            </span>
          </p>
        );
      } else {
        priceSection = <p>{priceEl}</p>;
      }
    }

    return (
      <div className={rootClassName}>
        {children}
        <Link
          to={route.book(isbn, signature_)}
          referrer={backText}
          onClick={onClick}
          className="collection-book-item__wrapper"
        >
          {image}
          <header className="collection-book-item__header">
            <p
              className={classNames('collection-book-item__heading', {
                'collection-book-item__heading--with-ranking': ranking_recommendations > 0,
              })}
            >
              {title}
            </p>
            <p className="collection-book-item__authors">{toRenderAuthors}</p>

            {priceSection}
          </header>
        </Link>
        {/* Interactive elements are overlayed using CSS to avoid nesting them inside the <a> tag. */}
        {hideActions ? null : (
          <div className="collection-book-item__actions-wrapper">
            <div className="collection-book-item__actions">
              <FavouriteButton contentId={isbn} contentType={CONTENT_TYPES.BOOK} />
              {withMenu ? (
                <ContentOptionsAsIcon
                  itemId={isbn}
                  itemType={CONTENT_TYPES.BOOK}
                  authors={authors}
                  book={item}
                  contentType={CONTENT_TYPES.BOOK}
                  collectionId={collectionId}
                  heading={item.title}
                  image={image}
                />
              ) : null}
            </div>
          </div>
        )}
        {hideActions ? null : (
          <BookRankOverlay
            ranking={ranking_recommendations}
            className="collection-book-item__rank"
          />
        )}
        {alacarte && !hideActions ? (
          <AlacarteStatusIndicator
            className="collection-book-item__alacarte"
            isbn={isbn}
            signature_={signature_}
            available={alacarte_available}
          />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  owned:
    typeof ownProps.owned === 'boolean'
      ? ownProps.owned
      : state.user.owned_books.includes(ownProps.item?.isbn),
});

export default connect(
  mapStateToProps,
  null,
)(withStyles(CssCollectionBookItem)(CollectionBookItem));
